import { useState, useRef, useEffect, useCallback } from 'react'
import { X } from 'lucide-react'
import { getZoom, EVT } from '@/lib/aparencia'

// Bolinha flutuante da Sofia: arrastável pra qualquer canto, clica pra abrir o chat
// e tem um × pra fechar (some até o usuário trazer de volta pelo ✨ no topo).
const SIZE = 56
const MARGIN = 20
const HIDE_KEY = 'sofia_bubble_hidden'
const POS_KEY = 'sofia_bubble_pos'

type Pos = { x: number; y: number }

// BUG: a bolinha é position:fixed posicionada por JS (style.left/top). Sob a
// propriedade CSS `zoom` do sistema, o elemento fixed vive no espaço de
// coordenadas ZOOMADO (viewport = innerWidth/zoom), mas usávamos innerWidth
// físico — então ela "fugia" pra fora da tela quando o zoom ≠ 100%.
// FIX (26/06): viewport() devolve a largura/altura já no espaço zoomado e a
// posição é re-clampada quando o zoom muda (evento EVT de aparência).
function viewport() {
  const z = getZoom() / 100 || 1
  return { w: window.innerWidth / z, h: window.innerHeight / z }
}

function defaultPos(): Pos {
  const v = viewport()
  return { x: v.w - SIZE - MARGIN, y: v.h - SIZE - MARGIN }
}

function clamp(p: Pos): Pos {
  const v = viewport()
  const maxX = v.w - SIZE - 4
  const maxY = v.h - SIZE - 4
  return { x: Math.min(Math.max(4, p.x), maxX), y: Math.min(Math.max(4, p.y), maxY) }
}

export default function SofiaBubble({ onOpen }: { onOpen: () => void }) {
  const [hidden, setHidden] = useState(() => localStorage.getItem(HIDE_KEY) === '1')
  const [pos, setPos] = useState<Pos>(() => {
    try {
      const saved = localStorage.getItem(POS_KEY)
      if (saved) return clamp(JSON.parse(saved))
    } catch { /* posição inválida — usa padrão */ }
    return defaultPos()
  })
  const drag = useRef<{ sx: number; sy: number; px: number; py: number; moved: boolean } | null>(null)

  // Traz a bolinha de volta quando o usuário clica no ✨ do topo
  useEffect(() => {
    const show = () => { localStorage.removeItem(HIDE_KEY); setHidden(false) }
    window.addEventListener('sofia-show-bubble', show)
    return () => window.removeEventListener('sofia-show-bubble', show)
  }, [])

  // Mantém dentro da tela ao redimensionar a janela OU ao mudar o zoom do
  // sistema (o espaço de coordenadas do fixed muda junto — ver viewport()).
  useEffect(() => {
    const reposicionar = () => setPos(p => clamp(p))
    window.addEventListener('resize', reposicionar)
    window.addEventListener(EVT, reposicionar)
    return () => {
      window.removeEventListener('resize', reposicionar)
      window.removeEventListener(EVT, reposicionar)
    }
  }, [])

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture?.(e.pointerId)
    drag.current = { sx: e.clientX, sy: e.clientY, px: pos.x, py: pos.y, moved: false }
  }, [pos])

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const d = drag.current
    if (!d) return
    const dx = e.clientX - d.sx
    const dy = e.clientY - d.sy
    if (!d.moved && Math.hypot(dx, dy) < 5) return // ainda é um clique, não arrasto
    d.moved = true
    setPos(clamp({ x: d.px + dx, y: d.py + dy }))
  }, [])

  const onPointerUp = useCallback(() => {
    const d = drag.current
    drag.current = null
    if (!d) return
    if (d.moved) {
      setPos(p => { localStorage.setItem(POS_KEY, JSON.stringify(p)); return p })
    } else {
      onOpen() // foi um clique: abre o chat
    }
  }, [onOpen])

  if (hidden) return null

  return (
    <div className="fixed z-40 select-none group" style={{ left: pos.x, top: pos.y }}>
      {/* Fechar */}
      <button
        onClick={() => { localStorage.setItem(HIDE_KEY, '1'); setHidden(true) }}
        title="Fechar a Sofia (volta pelo ✨ no topo)"
        aria-label="Fechar a Sofia"
        className="absolute -top-1.5 -right-1.5 z-10 h-5 w-5 rounded-full bg-white text-ink-500 shadow ring-1 ring-ink-200 flex items-center justify-center hover:bg-ink-100 hover:text-ink-700 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <X size={12} />
      </button>

      {/* Bolinha (arrastar / clicar) */}
      <button
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        title="Falar com a Sofia"
        aria-label="Abrir o chat da Sofia"
        style={{ touchAction: 'none' }}
        className="h-14 w-14 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 shadow-lg shadow-violet-500/30 ring-2 ring-white flex items-center justify-center cursor-grab active:cursor-grabbing hover:scale-105 transition-transform overflow-hidden"
      >
        <img src="/sofia.jpg" alt="Sofia" draggable={false} className="h-12 w-12 rounded-full object-cover pointer-events-none" />
      </button>

      {/* Rótulo no hover */}
      <span className="pointer-events-none absolute right-16 top-1/2 -translate-y-1/2 whitespace-nowrap rounded-full bg-ink-900 text-white text-xs px-2.5 py-1 opacity-0 group-hover:opacity-100 transition-opacity shadow">
        Sofia · sua ajudante
      </span>
    </div>
  )
}
